<template>
  <div
    id="sequence_detail_output"
    style="height: calc(100vh - (200px + var(--banner-height)))"
    class="sequence-detail-output overflow-y-scroll custom_scrollbar flex flex-col"
  >
    <div
      id="action_bar"
      class="flex flex-row justify-between gap-2 items-end px-4 pl-7 pt-2 mt-[-16px] ml-[-20px]"
    >
      <ViewManagerComponent
        v-model:views="views"
        :entity-type="entityTypes.SEQUENCE_ENTITY"
        :sequence-id="Number(sequenceId)"
        @fetchView="fetchViewData"
        @onNewViewCreated="handleNewCreate"
        @view-state-changed="handleViewStateChange"
        :api-variant="apiVariants.OUTREACH"
      />
      <div class="action_bar_buttons flex flex-row gap-2 pb-3">
        <CustomInput
          format="text"
          v-model="searchQuery"
          placeholder="Search contacts/leads"
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          size="size-4"
          alt="search_icon"
          :onChange="debouncedSearch"
        />
      </div>
    </div>

    <div class="mx-1 py-4">
      <ViewFilterComponent
        v-if="selection.selected_ids.length <= 0"
        :filters-options="filterOptions"
        :filter-data="filterData"
        :show-export-option="false"
        :current-view="views.current"
        :total-size="pagination.total"
        @filter-changed="handleFilterChange"
        :show-clear-all="false"
      />
    </div>

    <div
      v-if="loading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>
    <div v-else class="pr-4">
      <CustomTable
        :key="`sequence-table-${props.sequenceId}`"
        source="sequence_entity"
        v-if="outputData.length > 0"
        :headers="sequenceContactTableHeaders"
        :data="enhancedOutputData"
        :pagination="pagination"
        :show-actions-column="true"
        :actions="actions"
        :add-side-border="true"
        :custom-row="customRow"
        :custom-column-render="customColumnRender"
        :show-checkboxes="true"
        :show-select-all="true"
        height="calc(100vh - (350px + var(--banner-height)))"
        @update:pagination="handlePageChange"
        :initial-sort="initialSort"
        :selection="selection"
        :identifier-key="'id'"
        :view-id="views?.current?.id"
        :sequence-id="props.sequenceId"
        :sequences="sequences"
        :loading-removable-count="loadingRemovableCount"
        :show-remove-from-sequence-button="showRemoveFromSequenceButton"
        :all-selected-entities-removed="allSelectedEntitiesRemoved"
        :handle-bulk-remove-from-sequence="handleBulkRemoveFromSequence"
        :confirm-bulk-remove-from-sequence="confirmBulkRemoveFromSequence"
        :get-removable-count="getRemovableCount"
        @update:sort="updateSortOptions"
        @update:selection="handleSelectionUpdate"
        @update:sequence-entity-tracker="clearSequenceEntityTracker"
        @refresh="fetchSequenceLeadsAndContacts"
        :is-more-options-disabled="'actions_disabled'"
      />

      <div v-else class="flex flex-col items-center justify-center gap-2 mt-20">
        <ThemeImage
          :icon="imageNames.EMPTY_STATE_SECONDARY"
          alt="empty_state"
          class="size-20"
        />
        <span class="text-base text_color font-semibold"
          >Add contacts/leads to start {{ featureNames.SEQUENCES }}</span
        >
        <span class="text-base secondary_color text-center"
          >Add the contact/leads for whom you would<br />want to run this
          {{ featureNames.SEQUENCES }} for</span
        >
      </div>
    </div>
    <CustomModal
      :title="`Remove from ${featureNames.SEQUENCES}`"
      v-model:visible="showRemoveLeadModal"
      :width="'32vw'"
    >
      <span
        >Are you sure to remove this lead/contact from the
        {{ featureNames.SEQUENCES }}?</span
      >
      <template #footer>
        <a-button class="primary_medium" @click="confirmRemoveEntity"
          >Confirm
        </a-button>
      </template>
    </CustomModal>
    <CustomModal
      :title="`Pause ${featureNames.SEQUENCES} for lead/contact`"
      v-model:visible="showPauseModal"
      :width="'32vw'"
    >
      <span
        >Are you sure to pause this lead/contact for the
        {{ featureNames.SEQUENCES }}?</span
      >
      <template #footer>
        <a-button class="primary_medium" @click="confirmPauseEntity"
          >Confirm
        </a-button>
      </template>
    </CustomModal>
    <CustomModal
      :title="`Resume ${featureNames.SEQUENCES} for lead/contact`"
      v-model:visible="showResumeModal"
      :width="'32vw'"
    >
      <span
        >Are you sure to resume this lead/contact for the
        {{ featureNames.SEQUENCES }}?</span
      >
      <template #footer>
        <a-button class="primary_medium" @click="confirmResumeEntity"
          >Confirm
        </a-button>
      </template>
    </CustomModal>

    <SequenceContactDetails
      v-if="selectedEntity"
      :sequence-id="props.sequenceId"
      :entity-id="selectedEntity.entity_id"
      :entity-type="selectedEntity.entity_type"
      :execution-id="selectedEntity.id"
      :drawer-visible="contactDetailsDrawerVisible"
      @update:modelValue="contactDetailsDrawerVisible = false"
    />

    <CreateSequenceModal
      :width="'500px'"
      v-model:visible="showSequenceCreate"
      @sequence-created="handleSequenceCreated"
    />

    <SetupSenderDetailsModal
      v-model:visible="sequenceAddition.state.modalStates.setupSenderDetailsModal"
      :sequence-id="selectedSequenceId"
      :sequence-name="selectedSequenceName"
      :entity-count="
        sequenceAddition.state.pendingValidationData?.entity_list?.length || 0
      "
      @save="handleSenderSetup"
      @close="sequenceAddition.handleSetupModalClose"
    />

    <ValidationModal
      v-if="sequenceAddition.state.modalStates.validationModal"
      v-model:visible="sequenceAddition.state.modalStates.validationModal"
      :validation-results="sequenceAddition.state.validationResults"
      :validation-type="validationType"
      :sequence-id="selectedSequenceId"
      :sequence-name="selectedSequenceName"
      :force-close="sequenceAddition.state.forceCloseValidationModal"
      @add-to-journey="handleValidationCompletion"
      @close="sequenceAddition.state.modalStates.validationModal = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineExpose, computed } from "vue";
import SequenceService from "@/src/api/SequenceService.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";

import OutreachService from "@/src/api/OutreachService.js";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ViewFilterComponent from "@/src/components/view_components/ViewFilterComponent.vue";
import ViewManagerComponent from "@/src/components/view_components/ViewManagerComponent.vue";
import { apiVariants } from "@/src/constants/appConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { sequenceViewFilters } from "@/src/constants/filterConstants.js";
import SequenceContactDetails from "./SequenceContactDetails.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { updateFilterOptions } from "@/src/utils/utils";
import { debounce } from "lodash";
import CustomModal from "../../../components/CustomModal.vue";
import UserColumnRenderer from "@/src/components/UserColumnRenderer.vue";
import SenderColumnRenderer from "@/src/components/sequences_components/SenderColumnRenderer.vue";
import {
  sequenceStatuses,
  sequenceContactTableHeaders,
} from "@/src/constants/sequenceConstants";
import { featureNames } from "@/src/constants/globalConstants";
import { useSequenceAddition } from "@/src/composables/useSequenceAddition.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CreateSequenceModal from "@/src/views/sequence/actions/CreateSequenceModal.vue";
import SetupSenderDetailsModal from "@/src/components/sequences_components/SetupSenderDetailsModal.vue";
import ValidationModal from "@/src/views/sequence/ValidationModal.vue";

const props = defineProps({
  sequenceId: { type: [String, Number], required: true },
  refreshTrigger: { type: Number, default: 0 },
});

const router = useRouter();
const searchQuery = ref("");
const statusFilter = ref("");
const outputData = ref([]);
const loading = ref(true);
const showRemoveLeadModal = ref(false);
const showPauseModal = ref(false);
const showResumeModal = ref(false);
const entitiesToRemove = ref(null);
const entityToPause = ref(null);
const entityToResume = ref(null);
const contactDetailsDrawerVisible = ref(false);
const selectedEntity = ref(null);
const filterOptions = ref(sequenceViewFilters);
const views = ref({
  active: [],
  inactive: [],
  current: {},
});
const selection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const sequenceEntityTracker = ref(new Map());
const filterData = ref({});
const sortData = ref({});
const initialSort = ref({});
const pagination = ref({ current: 1, pageSize: 10, total: 0 });
const sequenceStatus = ref(sequenceStatuses.IN_ACTIVE);
const sequenceSteps = ref([]);

const removableEntitiesCount = ref(0);
const loadingRemovableCount = ref(false);

const sequenceAddition = useSequenceAddition();
const sequences = ref([]);
const showSequenceCreate = ref(false);
const selectedSequenceId = ref(null);
const selectedSequenceName = ref(null);
const validationType = ref(null);

const customColumnRender = {
  name: {
    component: UserColumnRenderer,
    props: (row) => ({
      row: row,
      sequenceSteps: sequenceSteps.value,
    }),
  },
  sender_info: {
    component: SenderColumnRenderer,
    props: (row) => ({
      row: row,
      sequenceSteps: sequenceSteps.value,
    }),
  },
};

const enhancedOutputData = computed(() => {
  return outputData.value.map((row) => ({
    ...row,
    actions_disabled:
      row.sequence_status === "Removed" || row.sequence_status === "REMOVED",
  }));
});

const actions = [
  {
    label: "Pause",
    handler: (row) => showPauseConfirm(row),
    icon: imageNames.PAUSE_GREY,
  },
  {
    label: "Resume",
    handler: (row) => showResumeConfirm(row),
    icon: imageNames.RESUME_GREY,
  },
  {
    label: "Remove",
    handler: (row) => showRemoveConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const showPauseConfirm = (entity) => {
  entityToPause.value = entity;
  showPauseModal.value = true;
};

const showResumeConfirm = (entity) => {
  entityToResume.value = entity;
  showResumeModal.value = true;
};

const showRemoveConfirm = (entity) => {
  entitiesToRemove.value = entity;
  showRemoveLeadModal.value = true;
};

const confirmRemoveEntity = async () => {
  if (entitiesToRemove.value) {
    try {
      const response = await SequenceService.removeEntitiesToSequence(
        props.sequenceId,
        entitiesToRemove.value.entity_id,
        entitiesToRemove.value.entity_type,
      );
      if (response.success) {
        message.success(`Removed from ${featureNames.SEQUENCES} successfully`);
        await fetchSequenceLeadsAndContacts();
      } else {
        message.error(
          `Failed to remove lead/contact from ${featureNames.SEQUENCES}`,
        );
      }
    } catch (error) {
      console.error(
        `Error removing lead/contact from ${featureNames.SEQUENCES}:`,
        error,
      );
      message.error(
        `An error occurred while removing the lead/contact from the ${featureNames.SEQUENCES}`,
      );
    } finally {
      showRemoveLeadModal.value = false;
      entitiesToRemove.value = null;
    }
  }
};

const confirmPauseEntity = async () => {
  if (entityToPause.value) {
    try {
      const response = await SequenceService.pauseEntityInSequence(
        props.sequenceId,
        entityToPause.value.entity_id,
        entityToPause.value.entity_type,
      );
      if (response.success) {
        message.success(
          `Contact/lead paused in ${featureNames.SEQUENCES} successfully`,
        );
        await fetchSequenceLeadsAndContacts();
      } else {
        message.error(
          `Failed to pause contact/lead in ${featureNames.SEQUENCES}`,
        );
      }
    } catch (error) {
      console.error(
        `Error pausing contact/lead in ${featureNames.SEQUENCES}:`,
        error,
      );
      message.error(
        `An error occurred while pausing the contact/lead in the ${featureNames.SEQUENCES}`,
      );
    } finally {
      showPauseModal.value = false;
      entityToPause.value = null;
    }
  }
};

const confirmResumeEntity = async () => {
  if (entityToResume.value) {
    try {
      const response = await SequenceService.resumeEntityInSequence(
        props.sequenceId,
        entityToResume.value.entity_id,
        entityToResume.value.entity_type,
      );
      if (response.success) {
        message.success(
          `Contact/lead resumed in ${featureNames.SEQUENCES} successfully`,
        );
        await fetchSequenceLeadsAndContacts();
      } else {
        message.error(
          `Failed to resume contact/lead in ${featureNames.SEQUENCES}`,
        );
      }
    } catch (error) {
      console.error(
        `Error resuming contact/lead in ${featureNames.SEQUENCES}:`,
        error,
      );
      message.error(
        `An error occurred while resuming the contact/lead in the ${featureNames.SEQUENCES}`,
      );
    } finally {
      showResumeModal.value = false;
      entityToResume.value = null;
    }
  }
};

const fetchSequenceDetails = async () => {
  try {
    const data = await SequenceService.fetchSequenceDetails(props.sequenceId);
    if (data.success) {
      sequenceStatus.value = data?.sequence?.status;
    } else {
      message.error(`Failed to fetch ${featureNames.SEQUENCES} details`);
    }
  } catch (error) {
    console.error(`Error fetching ${featureNames.SEQUENCES} details:`, error);
    message.error(
      `An error occurred while fetching ${featureNames.SEQUENCES} details`,
    );
  }
};

const fetchSequenceLeadsAndContacts = async () => {
  loading.value = true;

  try {
    const data = await SequenceService.fetchFilteredContactsAndLeads({
      viewId: views.value.current.id,
      sequenceId: props.sequenceId,
      page: pagination.value.current - 1,
      pageSize: pagination.value.pageSize,
      searchQuery: searchQuery.value,
    });

    if (data?.success) {
      outputData.value = data?.entities || [];
      pagination.value.total = data?.total || outputData.value.length;
      pagination.value.pageSize = data?.pageSize || 20;
      pagination.value.current = data?.currentPage || 1;
      initialSort.value = data?.sorts || {};
    } else {
      message.error(
        "Failed to fetch campaign contacts/leads. Please try again.",
      );
      console.error("Failed to fetch campaign contacts/leads:", data.errors);
    }
  } catch (error) {
    console.error("Error fetching campaign contacts/leads:", error);
    message.error("An error occurred while fetching campaign contacts/leads");
  } finally {
    loading.value = false;
  }
};

const applyFilter = async () => {
  try {
    const payload = {
      filters: filterData.value,
      sorts: sortData.value,
      page_size: pagination.value.pageSize,
      current_page: pagination.value.current,
    };

    const data = await OutreachService.applyFilter(
      views.value.current.id,
      payload,
    );

    if (data?.success) {
      filterData.value = data?.filters || {};

      if (pagination.value.selection) pagination.value.current = 1;

      await fetchSequenceLeadsAndContacts();
    } else {
      console.error("Failed to apply filter");
    }
  } catch (error) {
    console.error("Error applying filter:", error);
  }
};

const fetchViewData = async () => {
  try {
    const data = await SequenceService.fetchSequenceEntityViews(
      props.sequenceId,
    );

    if (data?.success) {
      views.value = {
        active: data.activeViews,
        inactive: data.inActiveViews,
        current: data.currentView,
      };

      filterData.value = data?.currentView?.filters || {};
      pagination.value.pageSize = data?.currentView?.page_size || 20;
      sortData.value = data?.currentView?.sorts || {};
      pagination.value.current = data?.currentView?.current_page || 1;

      await fetchSequenceLeadsAndContacts();
    } else {
      console.error("Failed to fetch View Data");
    }
  } catch (error) {
    console.error("Error fetching View Data:", error);
  }
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  applyFilter();
};

const handleFilterChange = ({ filter, updated_values }) => {
  if (updated_values === "" || updated_values) {
    filterData.value[filter] = updated_values;
  } else {
    if (filter in filterData.value) {
      delete filterData.value[filter];
    }
  }
  pagination.value.current = 1;
  applyFilter();
};

const handleSelectionUpdate = (newSelection) => {
  selection.value = newSelection;
  updateSequenceEntityTracker(newSelection);
};

const updateSequenceEntityTracker = (currentSelection) => {
  if (!currentSelection || !outputData.value) return;

  if (currentSelection.select_all) {
    outputData.value.forEach((entity) => {
      if (!entity?.id || !entity?.entity_id) return;

      const unselectedIds = currentSelection.unselected_ids || [];
      if (!unselectedIds.includes(entity.id)) {
        sequenceEntityTracker.value.set(entity.entity_id, {
          entity_type: entity.entity_type,
          sequence_status: entity.sequence_status,
          record_id: entity.id,
        });
      } else {
        sequenceEntityTracker.value.delete(entity.entity_id);
      }
    });
  } else {
    const selectedIds = currentSelection.selected_ids || [];

    outputData.value.forEach((entity) => {
      if (!entity?.id || !entity?.entity_id) return;

      const compositeKey = `${entity.id}:${entity.entity_id}`;
      if (selectedIds.includes(entity.id)) {
        sequenceEntityTracker.value.set(compositeKey, {
          entity_id: entity.entity_id,
          entity_type: entity.entity_type,
          sequence_status: entity.sequence_status,
          record_id: entity.id,
        });
      } else {
        sequenceEntityTracker.value.delete(compositeKey);
      }
    });
  }
};

const clearSequenceEntityTracker = () => {
  sequenceEntityTracker.value.clear();
};

const getSelectedEntities = () => {
  if (!outputData.value || !selection.value) return [];

  const identifierKey = "id";

  if (selection.value.select_all) {
    const unselectedIds = selection.value.unselected_ids || [];
    return outputData.value.filter(
      (entity) =>
        entity?.[identifierKey] != null &&
        !unselectedIds.includes(entity[identifierKey]),
    );
  } else {
    const selectedIds = selection.value.selected_ids || [];
    return outputData.value.filter(
      (entity) =>
        entity?.[identifierKey] != null &&
        selectedIds.includes(entity[identifierKey]),
    );
  }
};

const getRemovableCount = () => {
  if (selection.value.select_all) {
    return removableEntitiesCount.value || 0;
  }

  if (sequenceEntityTracker.value?.size > 0) {
    let count = 0;
    for (const [key, entityData] of sequenceEntityTracker.value) {
      if (entityData.sequence_status?.toLowerCase() !== "removed") {
        count++;
      }
    }
    return count;
  }

  const selectedEntities = getSelectedEntities();
  return selectedEntities.filter(
    (entity) => entity.sequence_status?.toLowerCase() !== "removed",
  ).length;
};

const showRemoveFromSequenceButton = computed(() => {
  return getRemovableCount() > 0;
});

const allSelectedEntitiesRemoved = computed(() => {
  return getRemovableCount() === 0;
});

const updateRemovableEntitiesCount = async (selectAll) => {
  if (selectAll && props.sequenceId) {
    try {
      const unselectedIds = selection.value?.unselected_ids || [];
      const response = await SequenceService.countRemovableEntities(
        props.sequenceId,
        unselectedIds,
      );

      if (response?.success && typeof response.removable_count === "number") {
        removableEntitiesCount.value = Math.max(0, response.removable_count);
      } else {
        removableEntitiesCount.value = 0;
      }
    } catch (error) {
      console.error(
        "Error getting removable contacts/leads count for button visibility:",
        error,
      );
      removableEntitiesCount.value = 0;
    }
  } else {
    removableEntitiesCount.value = 0;
  }
};

const handleBulkRemoveFromSequence = async () => {
  if (loadingRemovableCount.value) {
    return false;
  }
  if (!props.sequenceId) {
    message.error(`${featureNames.SEQUENCES} ID is required`);
    return false;
  }

  if (!selection.value) {
    message.error("No selection data available");
    return false;
  }

  if (selection.value.select_all) {
    loadingRemovableCount.value = true;
    try {
      const unselectedIds = selection.value.unselected_ids || [];
      const response = await SequenceService.countRemovableEntities(
        props.sequenceId,
        unselectedIds,
      );

      if (response?.success && typeof response.removable_count === "number") {
        removableEntitiesCount.value = Math.max(0, response.removable_count);
      } else {
        const totalEntities = pagination.value?.total || 0;
        const unselectedCount = unselectedIds.length;
        removableEntitiesCount.value = Math.max(
          0,
          totalEntities - unselectedCount,
        );
        console.warn(
          "Failed to get accurate removable count, showing total selected count",
        );
      }
    } catch (error) {
      console.error("Error getting removable contacts/leads count:", error);
      const totalEntities = pagination.value?.total || 0;
      const unselectedCount = (selection.value.unselected_ids || []).length;
      removableEntitiesCount.value = Math.max(
        0,
        totalEntities - unselectedCount,
      );
    } finally {
      loadingRemovableCount.value = false;
    }
  }

  return true; // Signal to show modal
};

const confirmBulkRemoveFromSequence = async () => {
  if (!props.sequenceId) {
    message.error(`${featureNames.SEQUENCES} ID is required`);
    return false;
  }

  if (!selection.value) {
    message.error("No selection data available");
    return false;
  }

  try {
    const payload = {
      selected_ids: selection.value.selected_ids || [],
      select_all: selection.value.select_all || false,
    };

    if (selection.value.select_all) {
      payload.unselected_ids = selection.value.unselected_ids || [];
    }

    const response = await SequenceService.removeMultipleEntitiesFromSequence(
      props.sequenceId,
      payload,
    );

    if (response?.success) {
      message.success(
        response.message ||
          `Contact(s)/Lead(s) removed from ${featureNames.SEQUENCES} successfully!`,
      );

      clearSequenceEntityTracker();

      await fetchSequenceLeadsAndContacts();

      // Clear selection
      selection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };

      return true;
    } else {
      const errorMessage =
        response?.message ||
        `Failed to remove contact(s)/lead(s) from ${featureNames.SEQUENCES}`;
      message.error(errorMessage);
      return false;
    }
  } catch (error) {
    console.error(`Error in bulk remove from ${featureNames.SEQUENCES}`, error);
    const errorMessage = `An error occurred while removing contact(s)/lead(s) from ${featureNames.SEQUENCES}`;
    message.error(errorMessage);
    return false;
  }
};

const handleNewCreate = () => {
  filterData.value = {};
  pagination.value = {
    current: 1,
    pageSize: 20,
    total: 0,
  };
  fetchSequenceLeadsAndContacts();
};

const updateSortOptions = (newSorts) => {
  sortData.value = newSorts;
  applyFilter();
};

const handleViewStateChange = () => {
  selection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  clearSequenceEntityTracker();
};

const onSearch = () => {
  pagination.value.current = 1;
  fetchSequenceLeadsAndContacts();
};

const debouncedSearch = debounce(onSearch, 300);

const customRow = (entity) => {
  return {
    onClick: () => {
      // If entity is removed, redirect to detail page instead of opening drawer
      if (entity.sequence_status?.toLowerCase() === "removed") {
        goToEntityDetail(entity);
      } else {
        // Open the contact details drawer for active entities
        openContactDetailsDrawer(entity);
      }
    },
  };
};

const openContactDetailsDrawer = (entity) => {
  if (!entity.entity_id || !entity.entity_type) {
    message.error("Contact/Lead not found");
    return;
  }

  selectedEntity.value = entity;
  contactDetailsDrawerVisible.value = true;
};

const goToEntityDetail = (entity) => {
  if (!entity.entity_id || !entity.entity_type) {
    message.error("Contact/Lead not found");
    return;
  }

  if (entity.entity_type === "Lead") {
    router.push({
      name: "lead-detail",
      params: { id: entity.entity_id },
    });
  } else if (entity.entity_type === "Contact") {
    router.push({
      name: "contact-detail",
      params: { id: entity.entity_id },
    });
  }
};

const fetchSequenceSteps = async () => {
  try {
    const response = await SequenceService.fetchSequenceSteps(props.sequenceId);
    if (response.success) {
      sequenceSteps.value = response.steps || [];
      const steps = response.steps.map((step) => ({
        name: `Step ${step.position}`,
        ...step,
        id: step.position,
      }));
      updateFilterOptions(filterOptions.value, "sequence_step", steps);
    } else {
      console.error("Failed to fetch sequence steps:", response.errors);
      message.error("Failed to fetch sequence steps. Please try again.");
    }
  } catch (error) {
    console.error("Error fetching sequence steps:", error);
    message.error("An error occurred while fetching sequence steps.");
  }
};

const fetchSequences = async (searchQuery = "") => {
  try {
    const response = await SequenceService.fetchSequences(0, 20, searchQuery);

    if (response?.success) {
      sequences.value = response?.sequences || [];
    } else {
      console.error("Failed to fetch sequences:", response.errors);
    }
  } catch (error) {
    console.error("Error fetching sequences:", error);
  }
};

const handleSequenceCreated = async (data) => {
  try {
    showSequenceCreate.value = false;
    
    if (data?.sequenceId) {
      selectedSequenceId.value = data.sequenceId;
      selectedSequenceName.value = data.sequenceName || "";

      const selectedEntities = getSelectedEntitiesWithTypes();

      if (selectedEntities.length === 0) {
        message.error("No entities selected to add to sequence");
        return;
      }

      const entityList = selectedEntities.map((entity) => ({
        id: entity.entity_id,
        entity_type: entity.entity_type.toLowerCase(),
      }));

      await sequenceAddition.addManualWithValidation(
        data.sequenceId,
        entityList,
      );
    }
  } catch (error) {
    console.error("Error handling sequence creation:", error);
    message.error("An error occurred while adding entities to the new sequence");
  }
};

const getSelectedEntitiesWithTypes = () => {
  if (!outputData.value || !selection.value) return [];

  const identifierKey = "id";

  if (selection.value.select_all) {
    const unselectedIds = selection.value.unselected_ids || [];
    return outputData.value.filter(
      (entity) =>
        entity?.[identifierKey] != null &&
        !unselectedIds.includes(entity[identifierKey]) &&
        entity.entity_id &&
        entity.entity_type,
    );
  } else {
    const selectedIds = selection.value.selected_ids || [];
    return outputData.value.filter(
      (entity) =>
        entity?.[identifierKey] != null &&
        selectedIds.includes(entity[identifierKey]) &&
        entity.entity_id &&
        entity.entity_type,
    );
  }
};

const handleSenderSetup = async (setupData) => {
  try {
    validationType.value =
      sequenceAddition.state.pendingValidationData?.validation_type;

    const result = await sequenceAddition.handleSenderSetup(
      setupData,
      "validation",
    );

    if (result.success) {
      console.log("Setup sender details completed via composable");
    }
  } catch (error) {
    console.error("Error in sender setup handler:", error);
    message.error("An error occurred during sender setup");
  }
};

const handleValidationCompletion = async (genericData) => {
  try {
    const result =
      await sequenceAddition.handleValidationCompletion(genericData);

    if (result.success) {
      selection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };
      clearSequenceEntityTracker();
      
      await fetchSequenceLeadsAndContacts();
      
      message.success(
        `Entities added to ${featureNames.SEQUENCES} successfully`,
      );
    }
  } catch (error) {
    console.error("Error in validation completion:", error);
  }
};

// Watch for refresh trigger changes
watch(
  () => props.refreshTrigger,
  (newValue, oldValue) => {
    if (newValue > oldValue && newValue > 0) {
      console.log("Refreshing contacts/leads due to trigger change");
      fetchSequenceLeadsAndContacts();
    }
  },
);

// Watch for data changes and update tracker accordingly
watch(
  () => outputData.value,
  (newData, oldData) => {
    if (
      newData !== oldData &&
      (selection.value.selected_ids.length > 0 || selection.value.select_all)
    ) {
      updateSequenceEntityTracker(selection.value);
    }
  },
);

const getSelectionWatchData = () => ({
  select_all: selection.value?.select_all,
  unselected_ids_length: selection.value?.unselected_ids?.length || 0,
  selected_ids_length: selection.value?.selected_ids?.length || 0,
  current_page: pagination.value?.current,
});

watch(
  () => getSelectionWatchData(),
  debounce(async ({ select_all }) => {
    await updateRemovableEntitiesCount(select_all);
  }, 300),
  { immediate: true },
);

onMounted(async () => {
  await fetchSequenceDetails();
  await fetchViewData();
  await fetchSequenceSteps();
  await fetchSequences();
});

defineExpose({
  fetchSequenceLeadsAndContacts,
});
</script>

<style scoped>
.sequence-detail-output {
  background-color: var(--background-color);
  color: var(--text-color);
}

#action_bar {
  position: relative;
}

#action_bar::after {
  content: "";
  position: absolute;
  inset: auto 0 0 0;
  height: 1px;
  background-color: var(--white-opacity-10);
}
</style>
